/**
 * Pure mapping: the API's register record + connector reading -> the domain
 * shapes the app renders (`Impianto`, `Istantanea`). No I/O, no clock of its
 * own (`adesso` is passed in), so the same wire input always maps the same way.
 *
 * A third-party plant (Hive) is read-only. The synthesised `Impianto` says so:
 * one `Sorgente` with `comandabile: false`, `centralina.tipo: 'terze_parti'`,
 * no generators, no technical channels. What the vendor does not send is not
 * invented — a missing temperature stays `mancante`/`null` (freshness never
 * faked, ADR-060). Hot water is deliberately NOT surfaced here yet
 * (`sanitario.presente = false`): Hive hot water is on/off with no temperature,
 * which the temperature-centric sanitario view does not fit — a separate
 * read-only view is a follow-up.
 */

import {
  PARAMETRI_PREDEFINITI,
  type Impianto,
  type Lettura,
  type ModalitaSistema,
  type ProgrammaOrario,
  type StatoImpianto,
  type StatoSorgente,
  type Zona,
} from '@emacomfort/domain';
import type { Istantanea } from '../DeviceClient';
import type { ImpiantoRegistro, LetturaImpiantoWire, ZonaLetturaWire } from './tipi';

const SORGENTE_ID = 'hive';
// The SLR receiver's setpoint envelope — a documented device fact, used only
// when Hive does not send the installer's configured limits (ADR-060).
const INTERVALLO_RICEVITORE: { min: number; max: number } = { min: 5, max: 32 };

function lettura(w: ZonaLetturaWire['temperatura']): Lettura {
  return { canale: w.canale, ts: w.ts ?? 0, v: w.v, u: w.u, q: w.q };
}

function zona(w: ZonaLetturaWire, ordinamento: number): Zona {
  return {
    id: w.id,
    nome: w.nome,
    icona: 'altro',
    ordinamento,
    canaleSonda: w.temperatura.canale,
    canaliAttuatore: [],
    tipoCarico: 'RELE_ON_OFF',
    // 🚨 SEMPRE abilitata, e non `modo !== 'OFF'`.
    //
    // `abilitata` significa "questa zona fa parte dell'impianto regolato" — la
    // spegne l'installatore, non la stagione. Il `modo` di Hive dice un'altra
    // cosa: se in questo momento sta scaldando. In agosto entrambe le zone
    // riportano OFF, e la conflazione cancellava l'intero impianto: zero zone
    // abilitate, quindi nessuna temperatura nella media ambiente ("— —") e un
    // sinottico che annunciava "0 zone". Una stanza ha una temperatura anche a
    // riscaldamento spento, ed è proprio quella che si è venuti a vedere.
    abilitata: true,
    // Hive sends a target in practice; on the rare absence fall back to the
    // device floor rather than a fabricated comfort value.
    setpoint: w.setpoint ?? INTERVALLO_RICEVITORE.min,
    sovrascritta: false,
    limiti: w.limiti ?? { ...INTERVALLO_RICEVITORE, impostatiDa: 'Hive (intervallo del ricevitore)' },
  };
}

function modalitaSistema(reading: LetturaImpiantoWire): ModalitaSistema {
  // A Hive is a heating system: on if any zone is not OFF, else off.
  return reading.zone.some((z) => z.modo !== 'OFF') ? 'RISCALDAMENTO' : 'SPENTO';
}

export function costruisciImpianto(reg: ImpiantoRegistro, reading: LetturaImpiantoWire): Impianto {
  const zone = reading.zone.map((z, i) => zona(z, i));
  return {
    id: reg.id,
    tenantId: reg.tenantId,
    nome: reg.nome,
    indirizzo: reg.indirizzo,
    cliente: reg.cliente,
    dataInstallazione: '',
    tecnicoReferente: '',
    centralina: {
      modello: reg.centralina.modello || 'Hive',
      firmware: reg.centralina.firmware,
      tipo: 'terze_parti',
      host: reg.centralina.host,
    },
    moduli: [{ id: SORGENTE_ID, tipo: 'CONNETTORE', nome: 'Hive', stato: 'ok', sorgenteId: SORGENTE_ID }],
    canali: [],
    zone,
    generatori: [],
    sorgenti: [{ id: SORGENTE_ID, nome: 'Hive', tipo: 'termostati', comandabile: false, descrizione: 'sorgenti.termostati' }],
    // Hot water is present when the source reports it, and it is on/off only —
    // no temperature (ADR-060). `setpoint` here is inert; the read-only view
    // (StatoSanitario.soloOnOff) never reads it.
    sanitario: { presente: reading.sanitario !== null, setpoint: 0, boostMinuti: 0 },
    vmc: { presente: false, modalita: 'SPENTA', sogliaUmidita: 0 },
    energia: { presente: false },
    parametri: PARAMETRI_PREDEFINITI,
    modalita: modalitaSistema(reading),
  };
}

export function statoSorgente(reading: LetturaImpiantoWire): StatoSorgente {
  return reading.sorgente;
}

export function costruisciStato(reading: LetturaImpiantoWire, adesso: number): StatoImpianto {
  const zone = reading.zone.map((z, i) => zona(z, i));
  return {
    ts: reading.sorgente.ultimoAggiornamento ?? adesso,
    modalita: modalitaSistema(reading),
    zone,
    sanitario: statoSanitario(reading, adesso),
    vmc: { modalita: 'SPENTA', attiva: false },
    zoneInDomanda: reading.zone.filter((z) => z.in_domanda).map((z) => z.id),
  };
}

function statoSanitario(reading: LetturaImpiantoWire, adesso: number): StatoImpianto['sanitario'] {
  const hw = reading.sanitario;
  if (!hw) return { setpoint: 0, boostFinoA: null, inCarica: false };
  return {
    // No temperature from Hive: setpoint is inert, soloOnOff drives the view.
    setpoint: 0,
    // Hive reports boost as remaining minutes; turn that into an approximate
    // end time from the reading's clock. Present-and-null stays null.
    boostFinoA: hw.boost_minuti != null ? adesso + hw.boost_minuti * 60_000 : null,
    inCarica: hw.acceso,
    soloOnOff: true,
  };
}

export function costruisciIstantanea(reading: LetturaImpiantoWire, adesso: number): Istantanea {
  const letture: Record<string, Lettura> = {};
  for (const z of reading.zone) {
    letture[z.temperatura.canale] = lettura(z.temperatura);
  }
  return {
    ts: reading.sorgente.ultimoAggiornamento ?? adesso,
    letture,
    attuatori: {},
    stato: costruisciStato(reading, adesso),
    energia: {},
    sorgenti: { [SORGENTE_ID]: reading.sorgente },
    allarmi: [],
  };
}

export function programmaZona(reading: LetturaImpiantoWire, zonaId: string): ProgrammaOrario {
  const z = reading.zone.find((x) => x.id === zonaId);
  const vuoto: ProgrammaOrario = { zonaId, attivo: false, giorni: { 0: [], 1: [], 2: [], 3: [], 4: [], 5: [], 6: [] } };
  if (!z) return vuoto;
  const giorni = vuoto.giorni;
  for (const [k, fasce] of Object.entries(z.programma.giorni)) {
    const i = Number(k);
    if (i >= 0 && i <= 6) giorni[i as 0 | 1 | 2 | 3 | 4 | 5 | 6] = fasce;
  }
  return { zonaId, attivo: z.programma.attivo, giorni };
}
